import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertTriangle, ArrowRight } from "lucide-react";

interface ServerSelectModalProps {
  open: boolean;
  serverList: any[];
  selectedServer: string | null;
  countdown: number | null;
  onSelect: (server: any) => void;
  onAnnotationMode: () => void;
  onSkip: () => void;
  modelError?: string | null;
}

export default function ServerSelectModal({
  open,
  serverList,
  selectedServer,
  countdown,
  onSelect,
  onAnnotationMode,
  onSkip,
  modelError,
}: ServerSelectModalProps) {
  return (
    <Modal open={open}>
      <Box
        id="select-ai-server"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          background: "var(--dk-surface-2, #f5f5f7)",
          borderRadius: "16px",
          border: "0.5px solid var(--dk-border, rgba(0,0,0,0.14))",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.6) inset",
          overflow: "hidden",
          outline: "none",
          fontFamily: `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif`,
        }}
      >
        <Box
          sx={{
            height: 40,
            background: "var(--dk-surface-glass, rgba(255,255,255,0.6))",
            backdropFilter: "blur(12px)",
            borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography sx={{ flex: 1, textAlign: "center", fontSize: "13px", fontWeight: 500, color: "var(--dk-text-2, #3a3a3c)" }}>
            AI 서버 선택
          </Typography>
        </Box>

        <Box sx={{ p: "20px 20px 0" }}>
          <Typography
            sx={{ fontSize: "17px", fontWeight: 700, color: "var(--dk-text, #1d1d1f)", letterSpacing: "-0.02em", mb: "3px" }}
          >
            학습 서버 선택
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "var(--dk-text-2, #6e6e73)", letterSpacing: "-0.01em", mb: "16px" }}>
            학습을 진행할 서버를 선택해주세요
          </Typography>

          <Box
            sx={{
              background: "var(--dk-surface, white)",
              borderRadius: "12px",
              border: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <TableContainer sx={{ maxHeight: 240 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Server", "Status", "ID", "Tag", ""].map((h, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "var(--dk-text-3, #aeaeb2)",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          // stickyHeader는 자체 background 필요
                          background: "var(--dk-surface-2, #fafafa)",
                          borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
                          py: "8px",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {serverList.map((server) => (
                    <TableRow
                      key={server.id}
                      sx={{
                        transition: "background 0.12s",
                        "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell
                        sx={{ fontSize: "13px", color: "var(--dk-text, #1d1d1f)", borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))" }}
                      >
                        {server.serverAddress}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background:
                                server.status === "online"
                                  ? "#28c840"
                                  : server.status === "busy"
                                    ? "#febc2e"
                                    : "#ff5f57",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color:
                                server.status === "online"
                                  ? "#16a34a"
                                  : server.status === "busy"
                                    ? "#ea580c"
                                    : "#dc2626",
                            }}
                          >
                            {server.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "13px", color: "var(--dk-text-2, #6e6e73)", borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))" }}
                      >
                        {server.identifier}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "13px", color: "var(--dk-text-2, #6e6e73)", borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))" }}
                      >
                        {server.tag}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))" }}>
                        <Button
                          id="select-server-btn"
                          variant="contained"
                          size="small"
                          onClick={() => onSelect(server)}
                          disabled={!!selectedServer}
                          startIcon={
                            selectedServer === server.serverAddress && countdown === null ? (
                              <CircularProgress size="12px" sx={{ color: "#fff" }} />
                            ) : null
                          }
                          sx={{
                            height: 28,
                            borderRadius: "7px",
                            fontSize: "12px",
                            fontWeight: 500,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            minWidth: "60px",
                            backgroundColor: "#2563eb",
                            boxShadow: "0 1px 4px rgba(37,99,235,0.3)",
                            "&:hover": { backgroundColor: "#1d4ed8" },
                            "&.Mui-disabled": { backgroundColor: "#e8e8ed", color: "var(--dk-text-3, #aeaeb2)", boxShadow: "none" },
                          }}
                        >
                          {selectedServer === server.serverAddress
                            ? countdown !== null
                              ? `${countdown}초...`
                              : "가져오는 중..."
                            : "선택"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* 검사모델 오류 안내 */}
        {modelError && (
          <Box
            sx={{
              m: "14px 20px 0",
              p: "10px 12px",
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
              background: "rgba(255,59,48,0.07)",
              border: "0.5px solid rgba(255,59,48,0.3)",
              borderRadius: "10px",
            }}
          >
            <AlertTriangle size={15} color="#ff3b30" style={{ flexShrink: 0, marginTop: 1 }} />
            <Box>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "#c9302c" }}>{modelError}</Typography>
              <Typography sx={{ fontSize: "12px", color: "var(--dk-text-2, #6e6e73)", mt: "2px" }}>
                아래 '서버 선택없이 진행하기'로 프로젝트 설정에 이동해 연결된 검사모델을 변경해주세요.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Footer */}
        {/* <Box sx={{ display: "flex", justifyContent: "flex-end", p: "14px 20px" }}> */}
        <Box sx={{ display: "flex", justifyContent: "space-between", p: "14px 20px" }}>
          <Button onClick={onAnnotationMode}>annotation mode</Button>
          <Button
            id="skip-select-server"
            onClick={onSkip}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              color: modelError ? "#007AFF" : "#6e6e73",
              fontWeight: modelError ? 600 : 400,
              textTransform: "none",
              borderRadius: "7px",
              "&:hover": { background: "rgba(0,0,0,0.05)", color: "var(--dk-text-2, #3a3a3c)" },
            }}
          >
            {modelError ? "서버 선택없이 진행하기 (검사모델 변경)" : "서버 선택없이 진행하기"}
            <ArrowRight size={12} />
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
